import React from 'react';
import { ArrowRight, Building2, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';

const MANUFACTURERS = [
  {
    parent: 'Daikin Industries',
    hq: 'Osaka, Japan',
    brands: [
      { name: 'Daikin', tier: 'Premium', link: '/daikin-ac-problems' },
      { name: 'Goodman', tier: 'Budget', link: '/goodman-ac-problems' },
      { name: 'Amana', tier: 'Mid-Premium', link: '/amana-ac-problems' },
    ],
    note: 'Daikin is the world\'s largest HVAC manufacturer. After acquiring Goodman in 2012, they control one of the widest price ranges in the industry — from budget Goodman units to premium Daikin inverter systems.',
    color: 'border-blue-400',
    bg: 'bg-blue-50',
  },
  {
    parent: 'Trane Technologies',
    hq: 'Dublin, Ireland (formerly Ingersoll Rand)',
    brands: [
      { name: 'Trane', tier: 'Premium', link: '/trane-ac-problems' },
      { name: 'American Standard', tier: 'Premium', link: '/american-standard-ac-problems' },
      { name: 'Oxbox', tier: 'Budget', link: '/oxbox-ac-problems' },
    ],
    note: 'Trane and American Standard are virtually identical products sold through different dealer networks. Oxbox is the builder-grade option manufactured with simplified designs at a lower price point.',
    color: 'border-emerald-400',
    bg: 'bg-emerald-50',
  },
  {
    parent: 'Carrier Global',
    hq: 'Palm Beach Gardens, FL',
    brands: [
      { name: 'Carrier', tier: 'Premium', link: '/carrier-ac-problems' },
      { name: 'Bryant', tier: 'Premium', link: '/bryant-ac-problems' },
      { name: 'Payne', tier: 'Budget', link: '/payne-ac-problems' },
    ],
    note: 'Willis Carrier invented modern air conditioning in 1902. Today, Carrier and Bryant share identical engineering and components. Payne serves the builder-grade market. Carrier also owns ICP (International Comfort Products) brands.',
    color: 'border-orange-400',
    bg: 'bg-orange-50',
  },
  {
    parent: 'Rheem Manufacturing',
    hq: 'Atlanta, GA',
    brands: [
      { name: 'Rheem', tier: 'Mid-Range', link: '/rheem-ac-problems' },
      { name: 'Ruud', tier: 'Mid-Range', link: '/ruud-ac-problems' },
    ],
    note: 'Rheem and Ruud are the same product with different branding. Rheem is sold through HVAC dealers and retailers, while Ruud is sold through plumbing and wholesale channels.',
    color: 'border-red-400',
    bg: 'bg-red-50',
  },
  {
    parent: 'Johnson Controls',
    hq: 'Cork, Ireland',
    brands: [
      { name: 'York', tier: 'Mid-Range', link: '/york-ac-problems' },
      { name: 'Luxaire', tier: 'Mid-Range', link: null },
      { name: 'Coleman', tier: 'Budget', link: null },
    ],
    note: 'Johnson Controls is primarily known for commercial HVAC. Their residential brands (York, Luxaire, Coleman) share components and are manufactured in the same facilities. York is the most widely recognized residential brand in the portfolio.',
    color: 'border-purple-400',
    bg: 'bg-purple-50',
  },
];

const PERFORMANCE_FACTORS = [
  {
    title: 'Correct System Sizing',
    desc: 'An oversized or undersized system wastes energy and fails to control humidity. In Louisiana, proper sizing accounts for humidity load — not just temperature. A Manual J calculation is essential.',
  },
  {
    title: 'Ductwork Condition',
    desc: 'Even the best HVAC system can\'t perform well with leaky or undersized ducts. In Louisiana, 20–30% of conditioned air can be lost through duct leaks, especially in unconditioned attic spaces.',
  },
  {
    title: 'Airflow Design',
    desc: 'Proper return air sizing, supply register placement, and static pressure design determine how evenly your home is cooled. Poor airflow causes hot spots and excessive runtime.',
  },
  {
    title: 'Installation Quality',
    desc: 'An expensive system installed poorly will underperform a budget system installed correctly. Refrigerant charge, electrical connections, drain line routing, and duct sealing all depend on installer skill.',
  },
];

const WhoMakesHvacPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="who-makes-heading"
          >
            Who Actually Makes HVAC Systems?
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-2">
            Brand Ownership Guide
          </p>
          <p className="text-slate-500 max-w-2xl mx-auto mb-8">
            Most homeowners don't realize that dozens of HVAC brands are actually
            manufactured by just a handful of parent companies. Understanding these
            relationships helps you make smarter purchasing decisions.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Get Your Instant HVAC Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Manufacturer Sections */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-8">
          {MANUFACTURERS.map((mfg) => (
            <div
              key={mfg.parent}
              className={`rounded-2xl border-l-4 ${mfg.color} bg-white shadow-sm overflow-hidden`}
              data-testid={`manufacturer-${mfg.parent.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className={`${mfg.bg} px-6 py-4`}>
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  <div>
                    <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {mfg.parent}
                    </h2>
                    <p className="text-sm text-slate-500">{mfg.hq}</p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-5">
                <div className="flex flex-wrap gap-3 mb-4">
                  {mfg.brands.map((brand) => (
                    <div key={brand.name} className="flex items-center gap-2">
                      {brand.link ? (
                        <a
                          href={brand.link}
                          className="px-3 py-1.5 bg-slate-50 rounded-full text-sm font-medium text-[#0F172A] hover:bg-[#0EA5E9]/10 hover:text-[#0EA5E9] transition-colors border border-slate-200"
                        >
                          {brand.name}
                        </a>
                      ) : (
                        <span className="px-3 py-1.5 bg-slate-50 rounded-full text-sm font-medium text-slate-400 border border-slate-100">
                          {brand.name}
                        </span>
                      )}
                      <span className="text-xs text-slate-400">({brand.tier})</span>
                    </div>
                  ))}
                </div>
                <p className="text-slate-600 text-sm">{mfg.note}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* What Matters More Than Brand */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What Matters More Than Brand
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            Brand alone does not determine system performance. These four factors have a
            bigger impact on your comfort, efficiency, and system longevity than the name
            on the equipment.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {PERFORMANCE_FACTORS.map((factor) => (
              <div key={factor.title} className="bg-white rounded-xl p-6" data-testid={`performance-factor-${factor.title.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-[#0EA5E9]" />
                  <h3 className="font-bold text-[#0F172A]">{factor.title}</h3>
                </div>
                <p className="text-slate-500 text-sm">{factor.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaway */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-[#0F172A] rounded-2xl p-8 text-white">
            <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The Bottom Line
            </h3>
            <p className="text-slate-300 leading-relaxed">
              When choosing a new HVAC system, don't get caught up in brand loyalty.
              A well-installed mid-range system will outperform a poorly-installed premium
              system every time. Focus on finding a qualified, experienced installer who
              performs proper load calculations and follows manufacturer specifications.
              The brand of equipment matters less than the quality of the installation.
            </p>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="prose prose-slate max-w-none">
            <p>
              Ready to explore your options? Get a free{' '}
              <a href="/instant-hvac-estimate-new-orleans" className="text-[#0EA5E9]">instant HVAC estimate</a>{' '}
              in minutes. Check{' '}
              <a href="/ac-replacement-cost-new-orleans" className="text-[#0EA5E9]">AC replacement costs in New Orleans</a>{' '}
              or learn about{' '}
              <a href="/repair-vs-replace-ac" className="text-[#0EA5E9]">when to repair vs. replace</a>.
              Not sure how old your system is?{' '}
              <a href="/check-ac-age" className="text-[#0EA5E9]">Decode your serial number</a>.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Get Your Instant HVAC Estimate
          </h2>
          <p className="text-slate-300 mb-8">
            See what a new system would cost for your home — free, fast, and no obligation.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Start My Instant Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhoMakesHvacPage;
